import { createRouter, createWebHistory, RouterView } from "vue-router";
import { HomeView, DeveloperView } from "@/views";
import { ManageTenantView, ManageUsersView, ApiRequestLogsView } from "@/views/Admin";
import {
  LoginView,
  ForgotPasswordView,
  ResetPasswordView,
  AcceptInviteView,
  ProfileView,
  EntraCallbackView,
  TenantSiteView,
  SiteView,
} from "@/views/Account";
import { InfoContainerView, InfoView, DeviceView } from "@/views/Info";
import {
  StocksView,
  StockChangesView,
  StockChangeView,
  StockDemandsView,
  StockChangesContainerView,
  OrderDemandsView,
} from "@/views/Stock";
import {
  OrdersView,
  OrderView,
  CreateOrderView,
  AcceptOrderView,
  ReceivingsView,
  ReceivingView,
  DeliveriesView,
  DeliveryView,
  DeliveryPrintView,
  CreateConsumptionView,
  StockReceiptView,
  StockIssueView,
  OrdersContainerView,
  ReceivingsContainerView,
  DeliveriesContainerView,
  ConsumptionsView,
  ConsumptionView,
  ConsumptionsContainerView,
} from "@/views/Transaction";
import {
  ItemLabelsContainerView,
  ItemLabelsForStockView,
  ItemLabelsForItemsView,
  ItemLabelsCaptureView,
  ItemLabelsView,
  ItemBoxPrintView,
} from "@/views/Labels";
import ExpiryView from "@/views/BusinessIntelligence/ExpiryView.vue";
import ItemIssuesView from "@/views/BusinessIntelligence/ItemIssuesView.vue";
import TranslationsService from "@/services/translations.ts";
import ItemContainerView from "@/views/Masterdata/ItemContainerView.vue";
import ItemsView from "@/views/Masterdata/ItemsView.vue";
import ItemView from "@/views/Masterdata/ItemView.vue";
import ForeignItemsView from "@/views/Masterdata/ForeignItemsView.vue";
import ItemBlocksContainerView from "@/views/Masterdata/ItemBlocksContainerView.vue";
import ItemBlocksView from "@/views/Masterdata/ItemBlocksView.vue";
import ItemBlockView from "@/views/Masterdata/ItemBlockView.vue";
import SuppliersContainerView from "@/views/Masterdata/SuppliersContainerView.vue";
import CreateSupplierView from "@/views/Masterdata/CreateSupplierView.vue";
import SuppliersView from "@/views/Masterdata/SuppliersView.vue";
import SupplierView from "@/views/Masterdata/SupplierView.vue";
import LabelPrinterContainerView from "@/views/Labels/LabelPrinterContainerView.vue";
import LabelPrintersView from "@/views/Labels/LabelPrintersView.vue";
import LabelPrinterView from "@/views/Labels/LabelPrinterView.vue";
import LabelTemplateView from "@/views/Labels/LabelTemplateView.vue";
import LabelTemplatesView from "@/views/Labels/LabelTemplatesView.vue";
import LabelTemplateContainerView from "@/views/Labels/LabelTemplateContainerView.vue";

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  linkActiveClass: "active",
  routes: [
    {
      path: "/:locale?",
      component: RouterView,
      beforeEnter: TranslationsService.routeMiddleware,
      children: [
        {
          path: "home",
          name: "home",
          component: HomeView,
          meta: { useTenantBgImg: true },
        },
        {
          path: "developer",
          name: "developer",
          component: DeveloperView,
          meta: { useTenantBgImg: true },
        },
        {
          path: "admin/manage-tenants",
          name: "manage-tenants",
          component: ManageTenantView,
          meta: { sitelessPage: true },
        },
        {
          path: "admin/manage-users",
          name: "manage-users",
          component: ManageUsersView,
          meta: { sitelessPage: true },
        },
        {
          path: "admin/api-request-logs",
          name: "api-request-logs",
          component: ApiRequestLogsView,
          meta: { sitelessPage: true },
        },
        {
          path: "account/login",
          name: "login",
          component: LoginView,
          meta: { anonymousAccess: true, sitelessPage: true, useTenantBgImg: true },
        },
        {
          path: "account/forgot-password",
          name: "forgot-password",
          component: ForgotPasswordView,
          meta: { anonymousAccess: true, useTenantBgImg: true },
        },
        {
          path: "account/reset-password",
          name: "reset-password",
          component: ResetPasswordView,
          meta: { anonymousAccess: true, useTenantBgImg: true },
        },
        {
          path: "account/accept-invite",
          name: "accept-invite",
          component: AcceptInviteView,
          meta: { anonymousAccess: true, useTenantBgImg: true },
        },
        {
          path: "account/profile",
          name: "profile",
          component: ProfileView,
          meta: { sitelessPage: true, useTenantBg: true },
        },
        {
          path: "account/site",
          name: "site",
          component: SiteView,
          meta: { sitelessPage: true, useTenantBgImg: true },
        },
        {
          path: "account/tenant-site",
          name: "tenant-site",
          component: TenantSiteView,
          meta: { sitelessPage: true, useTenantBgImg: true },
        },
        {
          path: "info",
          component: InfoContainerView,
          children: [
            {
              path: "",
              name: "info",
              component: InfoView,
            },
            {
              path: "device",
              name: "info-device",
              component: DeviceView,
              meta: { sitelessPage: true, useTenantBg: true },
            },
          ]
         },
        {
          path: "businessIntelligence/expiry",
          name: "biExpiry",
          component: ExpiryView,
        },
        {
          path: "businessIntelligence/ItemIssues",
          name: "biItemIssues",
          component: ItemIssuesView,
        },
        { path: "masterdata/items",
          component: ItemContainerView,
          children: [
            {
              path: "",
              name: "items",
              component: ItemsView,
            },
            {
              path: ":itemId",
              name: "item",
              component: ItemView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.itemId;
                const itemId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{ itemId: Number.parseInt(itemId, 0) || undefined },
                };
              },
            },
            {
              path: "create",
              name: "item-create",
              component: ItemView,
              props: (route) => {
                return {
                  ...route.params,
                  ...{ itemId: 0 },
                }
              }
            },
          ]
        },
        {
          path: "masterdata/foreign-items",
          name: "foreign-items",
          component: ForeignItemsView,
        },
        {
          path: "masterdata/itemblocks",
          component: ItemBlocksContainerView,
          children: [
            {
              path: "",
              name: "itemblocks",
              component: ItemBlocksView,
            },
            {
              path: ":itemBlockId",
              name: "itemblock",
              component: ItemBlockView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.itemBlockId;
                const itemBlockId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{ itemBlockId: Number.parseInt(itemBlockId, 0) || undefined },
                };
              },
            },
            {
              path: "create",
              name: "itemblock-create",
              component: ItemBlockView,
            },
          ],
        },
        {
          path: "masterdata/suppliers",
          component: SuppliersContainerView,
          children: [
            {
              path: "",
              name: "suppliers",
              component: SuppliersView,
            },
            {
              path: ":supplierId",
              name: "supplier",
              component: SupplierView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.supplierId;
                const supplierId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{ supplierId: Number.parseInt(supplierId, 0) || undefined },
                };
              },
            },
            {
              path: "create",
              name: "supplier-create",
              component: CreateSupplierView,
            },
          ],
        },
        {
          path: "transaction/orders",
          component: OrdersContainerView,
          children: [
            {
              path: "",
              name: "orders",
              component: OrdersView,
            },
            {
              path: ":orderId",
              name: "order",
              component: OrderView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.orderId;
                const orderId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{ orderId: Number.parseInt(orderId, 0) || undefined },
                };
              },
            },
            {
              path: "create",
              name: "order-create",
              component: CreateOrderView,
            },
            {
              path: ":orderId/accept",
              name: "order-accept",
              component: AcceptOrderView,
              meta: { anonymousAccess: true, sitelessPage: true, useTenantBgImg: true, hideSidebar: true },
              props: (route) => {
                const routeParam = route.params.orderId;
                const orderId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{
                    orderId: Number.parseInt(orderId, 0) || undefined,
                  },
                };
              },
            },
          ],
        },
        {
          path: "transaction/receivings",
          component: ReceivingsContainerView,
          children: [
            {
              path: "",
              name: "receivings",
              component: ReceivingsView,
            },
            {
              path: ":orderId",
              name: "receiving",
              component: ReceivingView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.orderId;
                const orderId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{ orderId: Number.parseInt(orderId, 0) || undefined },
                };
              },
            },
          ],
        },
        {
          path: "transaction/deliveries",
          component: DeliveriesContainerView,
          children: [
            {
              path: "",
              name: "deliveries",
              component: DeliveriesView,
            },
            {
              path: ":deliveryId",
              name: "delivery",
              component: DeliveryView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.deliveryId;
                const deliveryId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{
                    deliveryId: Number.parseInt(deliveryId, 0) || undefined,
                  },
                };
              },
            },
            {
              path: ":deliveryId/print",
              name: "delivery-print",
              component: DeliveryPrintView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.deliveryId;
                const deliveryId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{
                    deliveryId: Number.parseInt(deliveryId, 0) || undefined,
                  },
                };
              },
            },
          ],
        },
        {
          path: "transaction/consumption",
          component: ConsumptionsContainerView,
          children: [
            {
              path: "",
              name: "consumptions",
              component: ConsumptionsView,
            },
            {
              path: ":consumptionId",
              name: "consumption",
              component: ConsumptionView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.consumptionId;
                const consumptionId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{ consumptionId: Number.parseInt(consumptionId, 0) || undefined },
                };
              },
            },
            {
              path: "create",
              name: "consumption-create",
              component: CreateConsumptionView,
            },
          ],
        },
        {
          path: "transaction/stock/receipt",
          name: "stock-receipt",
          component: StockReceiptView,
        },
        {
          path: "transaction/stock/issue",
          name: "stock-issue",
          component: StockIssueView,
        },
        { path: "stock/stocks/:itemId?", name: "stocks", component: StocksView },
        {
          path: "stock/stock-changes",
          component: StockChangesContainerView,
          children: [
            {
              path: "",
              name: "stock-changes",
              component: StockChangesView,
            },
            {
              path: ":itemId",
              name: "stock-change",
              component: StockChangeView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.itemId;
                const itemId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{
                    itemId: Number.parseInt(itemId, 0) || undefined,
                  },
                };
              },
            },
          ],
        },
        {
          path: "stock/demand",
          name: "stock-demand",
          component: StockDemandsView,
        },
        {
          path: "order/demand",
          name: "order-demand",
          component: OrderDemandsView,
        },
        {
          path: "labels/item",
          component: ItemLabelsContainerView,
          children: [
            {
              path: "",
              name: "labels-item",
              component: ItemLabelsView,
            },
            {
              path: "capture",
              name: "labels-item-capture",
              component: ItemLabelsCaptureView,
            },
            {
              path: "stocks",
              name: "labels-item-stocks",
              component: ItemLabelsForStockView,
            },
            {
              path: "items",
              name: "labels-item-items",
              component: ItemLabelsForItemsView,
            },
          ],
        },
        {
          path: "labels/box",
          name: "labels-box",
          component: ItemBoxPrintView
        },
        {
          path: "label/printers",
          component: LabelPrinterContainerView,
          children: [
            {
              path: '',
              name: "label-printers",
              component: LabelPrintersView
            },
            {
              path: ':printerId',
              name: "label-printer",
              component: LabelPrinterView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.printerId;
                const printerId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{
                    printerId: Number.parseInt(printerId, 0) || undefined,
                  },
                };
              },
            },
            {
              path: 'create',
              name: "label-printer-create",
              component: LabelPrinterView
            },
          ]
        },
        {
          path: "label/templates",
          component: LabelTemplateContainerView,
          children: [
            {
              path: '',
              name: "label-templates",
              component: LabelTemplatesView
            },
            {
              path: ':templateId',
              name: "label-template",
              component: LabelTemplateView,
              meta: { siteSpecificEntity: true },
              props: (route) => {
                const routeParam = route.params.templateId;
                const templateId = Array.isArray(routeParam)
                  ? routeParam[0]!
                  : (routeParam as string);
                return {
                  ...route.params,
                  ...{
                    templateId: Number.parseInt(templateId, 0) || undefined,
                  },
                };
              },
            },
            {
              path: 'create',
              name: "label-template-create",
              component: LabelTemplateView
            },
          ]
        }
      ],
    },
    {
      path: "/entra-callback",
      name: "entra-callback",
      component: EntraCallbackView,
      meta: { anonymousAccess: true, sitelessPage: true },
    },
  ],
});
